// ────────────────────────────────────────────────────────────────
// src/pages/DashboardHome.js
// ────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import axios from 'axios'
import DailyTransactionsChart from '../components/DailyTransactionsChart'
import Chart from 'react-apexcharts'

/* ------------------------------------------------------------------ *
   Hook: return true when viewport < 768 px (mobile)
 * ------------------------------------------------------------------ */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  return isMobile
}

function DashboardHome() {
  const navigate = useNavigate()
  const { lang } = useOutletContext()

  /* ------------------------------------------------------------------ *
     Local-storage user & initial onboard flag
   * ------------------------------------------------------------------ */
  const storedUserInit   = JSON.parse(localStorage.getItem('user')) || null
  const alreadyOnboarded = storedUserInit?.onboarding_complete === true

  /* ------------------------------------------------------------------ *
     State
   * ------------------------------------------------------------------ */
  const [user, setUser]                   = useState(storedUserInit)
  const [isOnboarded, setIsOnboarded]     = useState(alreadyOnboarded)
  const [pendingRequests, setPending]     = useState(0)
  const [moneySent, setMoneySent]         = useState(0)
  const [moneyReceived, setMoneyReceived] = useState(0)
  const [directSent, setDirectSent]       = useState(0)
  const [paidFromRequests, setPaidFromReq] = useState(0)
  const [last30Transactions, setLast30Tx] = useState(0)

  /* ---------------- UX flag while link is generated --------------- */
  const [onboardingLinkLoading, setOnboardingLinkLoading] = useState(false) // ★ NEW

  const isMobile = useIsMobile()

  /* ------------------------------------------------------------------ *
     Translations
   * ------------------------------------------------------------------ */
  const translations = {
    en: {
      dashboard: 'Dashboard',
      completeOnboarding: 'Complete Onboarding',
      generatingLink: 'Generating link…',               // ★ NEW
      fewSeconds: 'It will only take a few seconds',
      sendMoney: 'Send Money',
      sendMoneyDesc: 'Pay friends and family',
      requestMoney: 'Request Money',
      requestMoneyDesc: 'Get paid fast',
      social: 'Social',
      socialDesc: 'Share an expense with friends',
      pendingRequests: 'Pending Requests',
      pendingRequestsDesc: 'Pending for you',
      pendingCountSuffix: 'pending',
      moneySent: 'Money Sent',
      moneyReceived: 'Money Received',
      daysTransactions: '30 Days Transactions',
      moneyRequestTx: 'Money Request Transactions',
      txBreakdown: 'Transaction Breakdown',
    },
    ja: {
      dashboard: 'ダッシュボード',
      completeOnboarding: 'オンボーディングを完了する',
      generatingLink: 'リンク生成中…',                // ★ NEW
      fewSeconds: '数秒ほどで完了します',
      sendMoney: '送金',
      sendMoneyDesc: '友人や家族に支払う',
      requestMoney: '支払い依頼',
      requestMoneyDesc: 'すぐにお金を受け取る',
      social: 'ソーシャル',
      socialDesc: '友人と費用を共有',
      pendingRequests: '保留中のリクエスト',
      pendingRequestsDesc: 'あなたが処理する保留中',
      pendingCountSuffix: '件の保留中',
      moneySent: '送金額',
      moneyReceived: '受取額',
      daysTransactions: '過去30日間の取引数',
      moneyRequestTx: 'リクエスト取引',
      txBreakdown: '取引の内訳',
    },
    pt: {
      dashboard: 'Painel',
      completeOnboarding: 'Concluir Onboarding',
      generatingLink: 'Gerando link…',                 // ★ NEW
      fewSeconds: 'Levará apenas alguns segundos',
      sendMoney: 'Enviar Dinheiro',
      sendMoneyDesc: 'Pague amigos e família',
      requestMoney: 'Solicitar Dinheiro',
      requestMoneyDesc: 'Receba pagamentos rapidamente',
      social: 'Social',
      socialDesc: 'Dividir despesas com amigos',
      pendingRequests: 'Solicitações Pendentes',
      pendingRequestsDesc: 'Pendentes para você',
      pendingCountSuffix: 'pendentes',
      moneySent: 'Dinheiro Enviado',
      moneyReceived: 'Dinheiro Recebido',
      daysTransactions: '30 Days Transactions',
      moneyRequestTx: 'Money Request Transactions',
      txBreakdown: 'Transaction Breakdown',
    },
  }

  const t = translations[lang] || translations.en

  /* ------------------------------------------------------------------ *
     Effects
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'))
    if (storedUser) {
      setUser(storedUser)
      checkStripeOnboarding(storedUser.id)
      fetchDashboardStats(storedUser)
    }
  }, [])

  const checkStripeOnboarding = async (userId) => {
    try {
      const { data } = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/stripe-status/${userId}`
      )
  
      setIsOnboarded(data.isOnboarded ?? false)
  
      /* ★ NEW – keep localStorage in sync so the sidebar unlocks
         without a full log-out / log-in cycle                        */
      if (data.isOnboarded) {
        const updatedUser = { ...user, onboarding_complete: true }
        setUser(updatedUser)
        localStorage.setItem('user', JSON.stringify(updatedUser))
      }
    } catch (err) {
      console.error('Error checking onboarding:', err)
      setIsOnboarded(false)
    }
  }

  const fetchDashboardStats = async (usr) => {
    try {
      const token = localStorage.getItem('token')

      /* money sent / received */
      const payRes = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/payments/stats/${usr.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const dbSent = Number(payRes.data.moneySent) || 0
      const dbRecv = Number(payRes.data.moneyReceived) || 0

      setDirectSent(dbSent)       // FIX ⇢ was dbRecv
      setMoneySent(dbSent)        // FIX ⇢ was dbRecv
      setMoneyReceived(dbRecv)    // FIX ⇢ was dbSent

      /* paid from requests */
      const reqPaidRes = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/requests/paidTotal/${usr.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setPaidFromReq(Number(reqPaidRes.data.paidTotal) || 0)

      /* pending requests */
      const pendRes = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/requests/pendingCount/${usr.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setPending(pendRes.data.count || 0)

      /* last-30-day tx */
      const tx30 = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/stats/transactionCountLast30/${usr.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      setLast30Tx(Number(tx30.data.count) || 0)
    } catch (err) {
      console.error('Error fetching stats:', err)
    }
  }

  /* ------------------------------------------------------------------ *
     Navigation helpers
   * ------------------------------------------------------------------ */
  const handleOnboarding = async () => {
    if (onboardingLinkLoading) return               // guard
    setOnboardingLinkLoading(true)                 // ★ NEW
    try {
      const { data } = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/auth/onboarding-link',
        { userId: user.id }
      )
      window.location.href = data.url
    } catch (err) {
      console.error('Error generating onboarding link:', err)
      setOnboardingLinkLoading(false)
    }
  }
  const handlePendingClick = () => navigate('/pending-requests')
  const handleSendMoney    = () => navigate('/send-money')
  const handleRequestMoney = () => navigate('/request-money')
  const handleSocial       = () => navigate('/social')

  /* ------------------------------------------------------------------ *
     Guards
   * ------------------------------------------------------------------ */
  if (!user)               return <p>Please log in first.</p>
  if (isOnboarded === null) return <p>Loading...</p>

  /* ------------------------------------------------------------------ *
     Not onboarded banner
   * ------------------------------------------------------------------ */
  if (!isOnboarded) {
    const cardClasses =
      `relative overflow-hidden bg-orange-100 dark:bg-orange-500/20
       border-orange-500/20 card rounded-md shadow-sm p-6
       ${onboardingLinkLoading
         ? 'opacity-60 cursor-not-allowed'
         : 'cursor-pointer hover:shadow'}
       group-data-[skin=bordered]:border-orange-500/20 max-w-md text-center`

    return (
      <div className="flex flex-col items-center justify-center h-full px-4">
        <div onClick={handleOnboarding} className={cardClasses}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 text-orange-200/50 dark:text-orange-500/20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5"></path>
            <path d="M12 5l-7 7 7 7"></path>
          </svg>

          {/* icon or spinner */}
          <div className="flex items-center justify-center w-12 h-12 bg-orange-500 rounded-md text-15 text-orange-50 relative z-10 mx-auto">
            {onboardingLinkLoading ? (
              <svg
                className="animate-spin h-6 w-6 text-orange-50"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-user-check"
              >
                <path d="M16 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M7 21v-2a4 4 0 0 1 3-3.87"></path>
                <circle cx="12" cy="7" r="4"></circle>
                <path d="m17 11 2 2 4-4"></path>
              </svg>
            )}
          </div>

          <h5 className="mt-5 mb-2 text-2xl font-semibold text-gray-800 dark:text-slate-200 relative z-10">
            {onboardingLinkLoading ? t.generatingLink : t.completeOnboarding}
          </h5>
          <p className="text-gray-600 dark:text-slate-200 relative z-10 mt-2">
            {t.fewSeconds}
          </p>
        </div>
      </div>
    )
  }

  /* ------------------------------------------------------------------ *
     Brand-new user – two big buttons (unchanged)
   * ------------------------------------------------------------------ */
  if (last30Transactions === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-4 gap-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-5xl w-full">
          {/* Send Money card */}
          <div
            onClick={handleSendMoney}
            className="relative overflow-hidden bg-green-100 dark:bg-green-500/20 border-green-500/20 card rounded-md shadow-sm p-6 cursor-pointer hover:shadow group-data-[skin=bordered]:border-green-500/20"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 text-green-200/50 dark:text-green-500/20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
            <div className="flex items-center justify-center w-12 h-12 bg-green-500 rounded-md text-15 text-green-50 relative z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-up-right"
              >
                <line x1="7" y1="17" x2="17" y2="7"></line>
                <polyline points="7 7 17 7 17 17"></polyline>
              </svg>
            </div>
            <h5 className="mt-5 mb-2 text-xl font-semibold text-gray-800 dark:text-slate-200 relative z-10">
              {t.sendMoney}
            </h5>
            <p className="text-slate-500 dark:text-slate-200 relative z-10">
              {t.sendMoneyDesc}
            </p>
          </div>

          {/* Request Money card */}
          <div
            onClick={handleRequestMoney}
            className="relative overflow-hidden bg-orange-100 dark:bg-orange-500/20 border-orange-500/20 card rounded-md shadow-sm p-6 cursor-pointer hover:shadow group-data-[skin=bordered]:border-orange-500/20"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 text-orange-200/50 dark:text-orange-500/20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5"></path>
              <path d="M12 5l-7 7 7 7"></path>
            </svg>
            <div className="flex items-center justify-center w-12 h-12 bg-orange-500 rounded-md text-15 text-orange-50 relative z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-down-left"
              >
                <line x1="17" y1="7" x2="7" y2="17"></line>
                <polyline points="17 17 7 17 7 7"></polyline>
              </svg>
            </div>
            <h5 className="mt-5 mb-2 text-xl font-semibold text-gray-800 dark:text-slate-200 relative z-10">
              {t.requestMoney}
            </h5>
            <p className="text-slate-500 dark:text-slate-200 relative z-10">
              {t.requestMoneyDesc}
            </p>
          </div>
        </div>
      </div>
    )
  }

  /* ------------------------------------------------------------------ *
     Dashboard with data (everything below unchanged)
   * ------------------------------------------------------------------ */
  const pieOptions = {
    labels: [t.pendingRequests, t.moneySent, t.moneyReceived],
    legend: { position: 'bottom' },
    dataLabels: { enabled: true },
    colors: ['#008ffb', '#00e396', '#feb019'],
  }
  const pieSeries = [paidFromRequests, directSent, moneyReceived]

  return (
    <div className="space-y-6 flex-grow h-full">
      {/* ─────────────────── MOBILE – 4-box grid ─────────────────── */}
      {isMobile && (
        <div className="grid grid-cols-1 gap-4 mb-4">
          {/* Send Money – mobile card */}
          <div
            onClick={handleSendMoney}
            className="relative overflow-hidden bg-green-100 dark:bg-green-500/20 border-green-500/20 card rounded-md shadow-sm p-3 cursor-pointer hover:shadow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 text-green-200/50 dark:text-green-500/20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
            <div className="flex items-center justify-center w-10 h-10 bg-green-500 rounded-md text-sm text-green-50 relative z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-up-right"
              >
                <line x1="7" y1="17" x2="17" y2="7"></line>
                <polyline points="7 7 17 7 17 17"></polyline>
              </svg>
            </div>
            <h5 className="mt-2 mb-1 text-lg font-semibold text-gray-800 dark:text-slate-200">
              {t.sendMoney}
            </h5>
            <p className="text-sm text-slate-500 dark:text-slate-200">
              {t.sendMoneyDesc}
            </p>
          </div>

          {/* Request Money – mobile card */}
          <div
            onClick={handleRequestMoney}
            className="relative overflow-hidden bg-orange-100 dark:bg-orange-500/20 border-orange-500/20 card rounded-md shadow-sm p-3 cursor-pointer hover:shadow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 text-orange-200/50 dark:text-orange-500/20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5"></path>
              <path d="M12 5l-7 7 7 7"></path>
            </svg>
            <div className="flex items-center justify-center w-10 h-10 bg-orange-500 rounded-md text-sm text-orange-50 relative z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-arrow-down-left"
              >
                <line x1="17" y1="7" x2="7" y2="17"></line>
                <polyline points="17 17 7 17 7 7"></polyline>
              </svg>
            </div>
            <h5 className="mt-2 mb-1 text-lg font-semibold text-gray-800 dark:text-slate-200">
              {t.requestMoney}
            </h5>
            <p className="text-sm text-slate-500 dark:text-slate-200">
              {t.requestMoneyDesc}
            </p>
          </div>

          {/* Social – mobile card */}
          <div
            onClick={handleSocial}
            className="relative overflow-hidden bg-sky-100 dark:bg-sky-500/20 border-sky-500/20 card rounded-md shadow-sm p-3 cursor-pointer hover:shadow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="absolute top-0 ltr:-right-10 rtl:-left-10 size-32 text-sky-200/50 dark:text-sky-500/20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 6h18"></path>
              <path d="M7 12h10"></path>
              <path d="M10 18h4"></path>
            </svg>
            <div className="flex items-center justify-center w-10 h-10 bg-sky-500 rounded-md text-sm text-sky-50 relative z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-coins"
              >
                <circle cx="8" cy="8" r="6"></circle>
                <path d="M18.09 10.37A6 6 0 1 1 10.34 18"></path>
                <path d="M7 6h1v4"></path>
                <path d="m16.71 13.88.7.71-2.82 2.82"></path>
              </svg>
            </div>
            <h5 className="mt-2 mb-1 text-lg font-semibold text-gray-800 dark:text-slate-200">
              {t.social}
            </h5>
            <p className="text-sm text-slate-500 dark:text-slate-200">
              {t.socialDesc}
            </p>
          </div>

          {/* Pending – mobile card */}
          <div
            onClick={handlePendingClick}
            className="relative overflow-hidden bg-purple-100 dark:bg-purple-500/20 card border-purple-500/20 rounded-md shadow-sm p-3 cursor-pointer hover:shadow"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-kanban absolute top-0 stroke-1 size-32 text-purple-200/50 dark:text-purple-500/20 ltr:-right-10 rtl:-left-10"
            >
              <path d="M6 5v11"></path>
              <path d="M12 5v6"></path>
              <path d="M18 5v14"></path>
            </svg>
            <div className="flex items-center justify-center bg-purple-500 rounded-md w-10 h-10 text-sm text-purple-50 relative z-10">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-users"
              >
                <path d="M16 21v-2a4 4 0 0 0-3-3.87"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h5 className="mt-2 mb-1 text-lg font-semibold text-gray-800 dark:text-slate-200">
              {t.pendingRequests}
            </h5>
            <p className="text-sm text-slate-500 dark:text-slate-200">
              {pendingRequests} {t.pendingRequestsDesc}
            </p>
          </div>
        </div>
      )}

      {/* ─────────────────── DESKTOP – stats strip ─────────────────── */}
      {!isMobile && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
          <div
            className="cursor-pointer bg-white rounded shadow p-4 hover:shadow-lg transition-shadow"
            onClick={handlePendingClick}
          >
            <h3 className="text-sm font-medium text-gray-500">
              {t.pendingRequests}
            </h3>
            <p className="text-2xl font-bold text-gray-800 mt-2">
              {pendingRequests} {t.pendingCountSuffix}
            </p>
          </div>
          <div className="bg-white rounded shadow p-4 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-medium text-gray-500">
              {t.moneySent}
            </h3>
            <p className="text-2xl font-bold text-gray-800 mt-2">
              ¥{Math.round(moneySent)}
            </p>
          </div>
          <div className="bg-white rounded shadow p-4 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-medium text-gray-500">
              {t.moneyReceived}
            </h3>
            <p className="text-2xl font-bold text-gray-800 mt-2">
              ¥{Math.round(moneyReceived)}
            </p>
          </div>
          <div className="bg-white rounded shadow p-4 hover:shadow-lg transition-shadow">
            <h3 className="text-sm font-medium text-gray-500">
              {t.daysTransactions}
            </h3>
            <p className="text-2xl font-bold text-gray-800 mt-2">
              {last30Transactions}
            </p>
          </div>
        </div>
      )}

      {/* ─────────────────── DESKTOP – charts ─────────────────── */}
      {!isMobile && (
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="bg-white rounded shadow p-4 lg:w-1/2">
            <h3 className="text-base font-semibold text-gray-700 mb-2">
              {t.moneyRequestTx}
            </h3>
            <DailyTransactionsChart userId={user.id} />
          </div>

          <div className="bg-white rounded shadow p-4 lg:w-1/2">
            <h3 className="text-base font-semibold text-gray-700 mb-2">
              {t.txBreakdown}
            </h3>
            <Chart
              options={pieOptions}
              series={pieSeries}
              type="pie"
              height="320"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardHome
